import { motion } from "framer-motion";
import { useState } from "react";
import { Eye, EyeOff, FileText, Home, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { useContent, useVisibleSections } from "@/content/ContentContext";
import { viewFor } from "@/components/room/registry";
import { Logo } from "./Logo";
import { LangToggle } from "./LangToggle";
import { useT } from "@/content/i18n";
import { CvDownload, DiscoveryCounter } from "./Extras";

const ease = [0.22, 1, 0.36, 1];

export const TopBar = ({ onReset, showHotspots, toggleHotspots, visited = [], onSelect }) => {
  const { profile, sections } = useContent();
  const contactId = sections.find((s) => s.type === "contact" && s.visible !== false)?.id;
  const t = useT();
  return (
  <motion.header
    className="pointer-events-none fixed left-0 right-0 top-0 z-40 flex items-start justify-between p-4 sm:p-6"
    initial={{ opacity: 0, y: -16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.9, delay: 0.6, ease }}
  >
    <div className="pointer-events-auto flex flex-col items-start gap-3">
    <button data-testid="brand-home-button" onClick={onReset} className="flex items-center gap-3 text-left">
      <Logo className="h-10 w-10" />
      <span className="hidden sm:block">
        <span className="block font-display text-lg leading-tight text-[#F7F4EE]">{profile.firstName} {profile.lastName}</span>
        <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-[#E6B877]">{profile.role}</span>
      </span>
    </button>
    <DiscoveryCounter visited={visited} />
    </div>
    <div className="pointer-events-auto flex flex-wrap items-center justify-end gap-2">
      <LangToggle />
      {contactId && (
        <button data-testid="topbar-contact-button" onClick={() => onSelect(contactId)} className="chip" aria-label={t("contactMe")} title={t("contactMe")}>
          <Mail className="h-4 w-4" />
          <span className="hidden sm:inline">{t("contactMe")}</span>
        </button>
      )}
      <CvDownload />
      <Link to="/cv" data-testid="topbar-classic-link" className="chip chip-gold" aria-label={t("cvOnly")} title={t("cvOnly")}>
        <FileText className="h-4 w-4" />
        <span className="hidden sm:inline">{t("cvOnly")}</span>
      </Link>
      <button data-testid="toggle-hotspots-button" onClick={toggleHotspots} className="chip" aria-label={t("showHotspots")} title={t("showHotspots")}>
        {showHotspots ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        <span className="hidden sm:inline">{t("hotspots")}</span>
      </button>
      <button data-testid="reset-camera-button" onClick={onReset} className="chip" aria-label={t("overview")} title={t("overview")}>
        <Home className="h-4 w-4" />
        <span className="hidden sm:inline">{t("overview")}</span>
      </button>
    </div>
  </motion.header>
  );
};

export const NavDock = ({ activeId, onSelect, hidden, shifted }) => {
  const sections = useVisibleSections();
  const [tip, setTip] = useState(null);
  const showTip = (e, label) => {
    const btn = e.currentTarget.getBoundingClientRect();
    const nav = e.currentTarget.closest("nav").getBoundingClientRect();
    setTip({ label, x: btn.left + btn.width / 2 - nav.left });
  };
  return (
  <motion.nav
    data-testid="nav-dock"
    className="dock-wrap fixed bottom-4 z-40 w-[calc(100%-24px)] max-w-fit sm:bottom-6"
    style={{ left: shifted ? "calc((100vw - 516px) / 2)" : "50%" }}
    initial={{ opacity: 0, y: 30, x: "-50%" }}
    animate={{ opacity: hidden ? 0 : 1, y: hidden ? 30 : 0, x: "-50%", pointerEvents: hidden ? "none" : "auto" }}
    transition={{ duration: 0.7, ease }}
  >
    {tip && !hidden && (
      <span className="dock-tip" style={{ left: tip.x }} data-testid="nav-dock-tooltip">{tip.label}</span>
    )}
    <div className="dock no-scrollbar flex gap-1 overflow-x-auto p-1.5 xl:overflow-visible" onScroll={() => setTip(null)}>
      {sections.map((s) => {
        const Icon = viewFor(s).icon;
        const id = s.id;
        return (
          <button
            key={id}
            data-testid={`nav-item-${id}`}
            onClick={() => onSelect(id)}
            onPointerEnter={(e) => { if (e.pointerType === "mouse") showTip(e, s.label); }}
            onPointerLeave={() => setTip(null)}
            onFocus={(e) => { if (e.currentTarget.matches(":focus-visible")) showTip(e, s.label); }}
            onBlur={() => setTip(null)}
            className={`dock-item group ${activeId === id ? "is-active" : ""}`}
            aria-label={s.label}
          >
            <Icon className="h-[18px] w-[18px]" strokeWidth={1.6} />
          </button>
        );
      })}
    </div>
  </motion.nav>
  );
};

export const Hint = () => {
  const t = useT();
  return (
  <motion.p
    className="pointer-events-none fixed bottom-7 left-6 z-30 hidden font-mono text-[11px] uppercase tracking-[0.2em] text-[#C5BDB3] xl:block"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay: 2.2, duration: 1 }}
  >
    {t("hint")}
  </motion.p>
  );
};
